/* TaskCardKit — a self-contained copy of the Tasks module's "Today grid" card
   and its satellites (quick-actions menu, collaborator avatars, smart popover)
   so the Planner can render FULL-PARITY task cards without importing from — or
   refactoring — the large TasksAppShell. The Planner already renders inside
   `.notes-scope`, so all `--n-*` design tokens resolve identically here.

   Pieces exported:
     • setViewerId(id)                     — hide the viewer's own avatar
     • dueStatusOf / dueBorderCls / dueIso — due helpers (parity with Tasks)
     • AvatarStack / AvatarColumn          — collaborator read-receipt avatars
     • SmartPopover                        — viewport-aware anchored popover
     • TaskQuickActions                    — right-click / long-press menu
     • RichTaskCard                        — the full-parity grid card
*/
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Check, CheckCircle2, Loader2, Bell, Clock, Eye, X, RotateCcw, Flag,
  CalendarDays, MessageSquare, Send, ArrowLeft, Users2, NotebookText,
  AtSign, ListChecks, Hash,
} from 'lucide-react';

// ---- helpers (copied verbatim from TasksAppShell for exact parity) --------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
export const dueStatusOf = (t) => {
  if (!t || !t.due_at) return 'none';
  const dd = new Date(t.due_at).getTime();
  if (isNaN(dd)) return 'none';
  const now = new Date();
  if (dd < startOfDay(now).getTime()) return t.is_completed ? 'none' : 'overdue';
  if (dd <= endOfDay(now).getTime()) return 'today';
  return 'future';
};
export const dueBorderCls = (st) => (st === 'overdue' ? 'border-[var(--n-danger)]' : st === 'today' ? 'border-[var(--n-success)]' : 'border-[var(--n-border)]');
const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return ''; return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
export const dueIso = (dateStr) => { if (!dateStr) return null; const d = new Date(`${dateStr}T00:00:00`); if (isNaN(d.getTime())) return null; return endOfDay(d).toISOString(); };

// Gold accent for the "shared with me" chip.
const N_GOLD = '#C99A2E';
const N_GOLD_BG = 'rgba(201,154,46,0.16)';
const N_GOLD_BORDER = 'rgba(201,154,46,0.48)';

const PRIO = {
  high: { label: 'High', cls: 'text-[var(--n-danger)]' },
  medium: { label: 'Medium', cls: 'text-[var(--n-accent-2)]' },
  normal: { label: 'Normal', cls: 'text-[var(--n-muted)]' },
};
const PRIO_ORDER = ['high', 'medium', 'normal'];

// Current viewer id — set by the Planner shell so avatar components can hide
// the viewer's own avatar from collaborator stacks.
let VIEWER_ID = null;
export const setViewerId = (id) => { VIEWER_ID = id || null; };

// ---- collaborator avatars -------------------------------------------------
export const AvatarStack = ({ people = [], size = 20, max = 3, testid = 'task-avatar-stack' }) => {
  const list = (people || []).filter((u) => u && u.id !== VIEWER_ID);
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span className="flex -space-x-1.5" data-testid={testid}>
      {shown.map((u) => {
        const seen = u.is_owner || u.has_read;
        let seenWhen = null;
        if (seen && u.read_at) {
          try { seenWhen = new Date(u.read_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { seenWhen = null; }
        }
        const title = `${u.name}${u.is_owner ? ' (owner)' : ''}${seen ? (seenWhen ? ` · seen ${seenWhen}` : ' · seen') : ' · not seen yet'}`;
        return (
          <span key={u.id} title={title}
            data-testid={`${testid}-avatar${seen ? '-seen' : ''}`}
            style={{ height: size, width: size }}
            className={`grid place-items-center rounded-full border-2 border-[var(--n-surface)] text-[9px] font-bold text-white ${seen ? 'bg-[var(--n-success)]' : 'bg-[var(--n-accent)]'}`}>
            {u.initial || (u.name || '?').charAt(0).toUpperCase()}
          </span>
        );
      })}
      {extra > 0 && (
        <span style={{ height: size, width: size }} className="grid place-items-center rounded-full border-2 border-[var(--n-surface)] bg-[var(--n-canvas-2)] text-[9px] font-bold text-[var(--n-muted)]">+{extra}</span>
      )}
    </span>
  );
};

export const AvatarColumn = ({ people = [], size = 18, testid = 'task-avatar-column' }) => {
  const list = (people || []).filter((u) => u && u.id !== VIEWER_ID);
  if (!list.length) return null;
  return (
    <span data-testid={testid}
      className="flex shrink-0 flex-col items-center justify-center gap-1 self-stretch border-l border-[var(--n-border)] pl-2">
      {list.map((u) => {
        const seen = u.is_owner || u.has_read;
        let seenWhen = null;
        if (seen && u.read_at) {
          try { seenWhen = new Date(u.read_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { seenWhen = null; }
        }
        const title = `${u.name}${u.is_owner ? ' (owner)' : ''}${seen ? (seenWhen ? ` · seen ${seenWhen}` : ' · seen') : ' · not seen yet'}`;
        return (
          <span key={u.id} title={title}
            data-testid={`${testid}-avatar${seen ? '-seen' : ''}`}
            style={{ height: size, width: size }}
            className={`grid shrink-0 place-items-center rounded-full border-2 border-[var(--n-surface)] text-[9px] font-bold text-white ${seen ? 'bg-[var(--n-success)]' : 'bg-[var(--n-accent)]'}`}>
            {u.initial || (u.name || '?').charAt(0).toUpperCase()}
          </span>
        );
      })}
    </span>
  );
};

// ---- viewport-aware anchored popover --------------------------------------
export const SmartPopover = ({
  anchor, width = 288, onClose, testId, backdropTestId, closeGuardMs = 0,
  zPanel = 'z-[301]', zBackdrop = 'z-[300]', children,
}) => {
  const ref = useRef(null);
  const mountedAt = useRef(Date.now());
  const [pos, setPos] = useState(null);

  const ax = (anchor.left + anchor.right) / 2;
  const aTop = anchor.top;
  const aBottom = anchor.bottom;
  const isPoint = anchor.left === anchor.right;
  const aLeft = anchor.left;

  const reflow = useCallback(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 8;
    const gap = 6;
    const rect = el.getBoundingClientRect();
    const h = rect.height;
    const w = rect.width || width;
    const below = vh - aBottom - m;
    const above = aTop - m;
    let top;
    if (below >= h + gap) top = aBottom + gap;
    else if (above >= h + gap) top = aTop - h - gap;
    else top = (vh - h) / 2;
    top = Math.max(m, Math.min(top, vh - h - m));
    let left = isPoint ? ax - w / 2 : aLeft;
    if (left + w > vw - m) left = vw - w - m;
    left = Math.max(m, left);
    setPos({ top: Math.round(top), left: Math.round(left) });
  }, [ax, aTop, aBottom, aLeft, isPoint, width]);

  useLayoutEffect(() => {
    reflow();
    const el = ref.current;
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(() => reflow());
      ro.observe(el);
    }
    window.addEventListener('resize', reflow);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', reflow);
    };
  }, [reflow]);

  return (
    <>
      <div className={`fixed inset-0 ${zBackdrop}`} data-testid={backdropTestId}
        onClick={() => { if (closeGuardMs && Date.now() - mountedAt.current < closeGuardMs) return; onClose(); }}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div ref={ref} data-testid={testId} role="menu"
        className={`fixed ${zPanel} flex flex-col overflow-y-auto n-scroll rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]`}
        style={{ width: `min(${width}px, calc(100vw - 16px))`, maxHeight: '70vh', top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? 'visible' : 'hidden' }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        {children}
      </div>
    </>
  );
};

// ---- Ping sub-screen (owner nudges collaborators) -------------------------
const pingedAgoLabel = (iso) => {
  if (!iso) return 'Pinged recently';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return 'Pinged recently';
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `Pinged ${h} hour${h > 1 ? 's' : ''} ago`;
  const mins = Math.max(1, Math.floor(diff / 60000));
  return `Pinged ${mins} min${mins > 1 ? 's' : ''} ago`;
};

const PingList = ({ task, onFetchPingStatus, onPing, onConfirmed }) => {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmName, setConfirmName] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await onFetchPingStatus(task);
        if (alive) setUsers((data && data.users) || []);
      } catch {
        if (alive) { setUsers([]); setError('Could not load collaborators.'); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [task, onFetchPingStatus]);

  const doPing = async (u) => {
    if (!u || !u.pingable || busyId) return;
    setBusyId(u.id); setError(null);
    try {
      await onPing(task, u.id);
      setConfirmName(u.name);
      timer.current = window.setTimeout(() => { if (onConfirmed) onConfirmed(); }, 1500);
    } catch (e) {
      const status = e && e.response && e.response.status;
      setUsers((prev) => (prev || []).map((x) => (x.id === u.id
        ? { ...x, pingable: false, pinged_at: status === 429 ? (x.pinged_at || new Date().toISOString()) : x.pinged_at, has_read: status === 400 ? true : x.has_read }
        : x)));
      setError(status === 429 ? `${u.name} was already pinged in the last 24 hours.`
        : status === 400 ? `${u.name} can no longer be pinged.`
          : 'Could not send the ping.');
    } finally {
      setBusyId(null);
    }
  };

  if (confirmName) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-6 text-center" data-testid="ping-confirm">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-success)]"><CheckCircle2 size={22} /></span>
        <span className="text-[13px] font-semibold text-[var(--n-ink)]">Ping sent to {confirmName}</span>
      </div>
    );
  }
  if (loading) {
    return <div className="flex items-center justify-center py-8 text-[var(--n-muted-2)]" data-testid="ping-loading"><Loader2 size={18} className="animate-spin" /></div>;
  }
  return (
    <>
      {(users || []).length === 0 ? (
        <div className="py-6 text-center text-[12.5px] text-[var(--n-muted)]" data-testid="ping-empty">No collaborators to ping.</div>
      ) : (
        <div className="flex flex-col gap-1" data-testid="ping-list">
          {(users || []).map((u) => {
            const cooldown = !u.pingable && !!u.pinged_at;
            const seen = !u.pingable && !u.pinged_at && !!u.has_read;
            const busy = busyId === u.id;
            return (
              <button key={u.id} type="button" data-testid="ping-user" data-pingable={u.pingable ? 'true' : 'false'}
                disabled={!u.pingable || busy} onClick={() => doPing(u)}
                title={u.pingable ? `Ping ${u.name}` : cooldown ? pingedAgoLabel(u.pinged_at) : seen ? 'Already opened this task' : u.name}
                className={`n-focus flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors ${u.pingable ? 'hover:bg-[var(--n-canvas-2)]' : 'cursor-not-allowed opacity-60'}`}>
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white ${u.pingable ? 'bg-[var(--n-accent)]' : 'bg-[var(--n-muted-2)]'}`}>{u.initial || (u.name || '?').charAt(0).toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{u.name}</span>
                  {cooldown && <span className="block truncate text-[11px] text-[var(--n-muted-2)]">{pingedAgoLabel(u.pinged_at)}</span>}
                  {seen && <span className="block truncate text-[11px] text-[var(--n-muted-2)]">Already opened</span>}
                </span>
                <span className="shrink-0">
                  {busy ? <Loader2 size={16} className="animate-spin text-[var(--n-muted-2)]" />
                    : u.pingable ? <Bell size={16} className="text-[var(--n-accent)]" />
                      : cooldown ? <Clock size={15} className="text-[var(--n-muted-2)]" />
                        : seen ? <Eye size={15} className="text-[var(--n-muted-2)]" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {error && <div className="mt-2 rounded-lg bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--n-danger)]" data-testid="ping-error">{error}</div>}
    </>
  );
};

// ---- Right-click / long-press quick actions -------------------------------
export const TaskQuickActions = ({ task, position, onClose, onToggleComplete, onSetDue, onSetPriority, onPostComment, onFetchPingStatus, onPing }) => {
  const [dueVal, setDueVal] = useState(task.due_at ? toLocalDate(task.due_at) : '');
  const [prio, setPrio] = useState(task.priority || 'normal');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const done = !!task.is_completed;

  const canPing = !!task.is_shared && !task.shared_by;
  const [screen, setScreen] = useState('main');

  const applyQuickDue = (offsetDays) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offsetDays);
    const s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setDueVal(s);
    onSetDue(task, dueIso(s));
  };

  const submitComment = async () => {
    const body = comment.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      await onPostComment(task, body);
      setComment('');
      setPosted(true);
      window.setTimeout(() => onClose(), 550);
    } catch { /* keep text so the user can retry */ } finally { setPosting(false); }
  };

  const anchor = { top: position.y, bottom: position.y, left: position.x, right: position.x };

  return (
    <SmartPopover anchor={anchor} width={288} onClose={onClose}
      testId="task-quick-actions" backdropTestId="task-quick-actions-backdrop"
      closeGuardMs={400} zPanel="z-[301]" zBackdrop="z-[300]">
      {screen === 'ping' ? (
        <div className="p-3" data-testid="task-quick-actions-ping-screen">
          <div className="mb-2.5 flex items-center gap-2">
            <button type="button" onClick={() => setScreen('main')} data-testid="task-quick-actions-ping-back"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><ArrowLeft size={15} /></button>
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--n-ink)]">Ping a collaborator</span>
            <button type="button" onClick={onClose} data-testid="task-quick-actions-ping-close"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
          </div>
          <PingList task={task} onFetchPingStatus={onFetchPingStatus} onPing={onPing} onConfirmed={() => setScreen('main')} />
        </div>
      ) : (
        <div className="p-3">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--n-ink)]" data-testid="task-quick-actions-title">{task.title || 'Untitled task'}</span>
            <button type="button" onClick={onClose} data-testid="task-quick-actions-close"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
          </div>

          <button type="button" data-testid="task-quick-actions-toggle-complete"
            onClick={() => { onToggleComplete(task); onClose(); }}
            className={`n-focus mb-2.5 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors ${done
              ? 'border border-[var(--n-border)] bg-[var(--n-canvas-2)] text-[var(--n-ink-soft)] hover:bg-[var(--n-surface)]'
              : 'bg-[var(--n-accent)] text-white hover:opacity-90'}`}>
            {done ? <><RotateCcw size={15} /> Mark as not done</> : <><CheckCircle2 size={15} /> Mark as done</>}
          </button>

          {canPing && (
            <button type="button" data-testid="task-quick-actions-ping" onClick={() => setScreen('ping')}
              className="n-focus mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--n-border)] px-3 py-2.5 text-[13px] font-semibold text-[var(--n-ink-soft)] transition-colors hover:border-[var(--n-accent)] hover:text-[var(--n-accent)]">
              <Bell size={15} /> Ping a collaborator
            </button>
          )}

          <div className="mb-3">
            <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
              <Flag size={12} /> Priority
            </div>
            <div className="flex w-full rounded-xl border border-[var(--n-border)] p-0.5" data-testid="task-quick-actions-priority">
              {PRIO_ORDER.map((p) => (
                <button key={p} type="button" data-testid={`task-quick-actions-priority-${p}`}
                  onClick={() => { setPrio(p); onSetPriority(task, p); }}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold transition-colors ${prio === p
                    ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]'
                    : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                  <Flag size={11} className={prio === p ? PRIO[p]?.cls : ''} />{PRIO[p]?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
              <CalendarDays size={12} /> Due date
            </div>
            <div className="flex items-center gap-1.5">
              <input type="date" value={dueVal} data-testid="task-quick-actions-due-input"
                onChange={(e) => { setDueVal(e.target.value); onSetDue(task, e.target.value ? dueIso(e.target.value) : null); }}
                className="n-focus min-w-0 flex-1 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] text-[var(--n-ink)]" />
              {dueVal && (
                <button type="button" data-testid="task-quick-actions-due-clear" title="Clear due date"
                  onClick={() => { setDueVal(''); onSetDue(task, null); }}
                  className="n-focus grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[var(--n-border)] text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={14} /></button>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="task-quick-actions-snooze">
              {[['Tomorrow', 1, 'tomorrow'], ['Next week', 7, 'next-week']].map(([label, off, key]) => (
                <button key={key} type="button" data-testid={`task-quick-actions-snooze-${key}`}
                  onClick={() => applyQuickDue(off)}
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[11.5px] font-medium text-[var(--n-ink-soft)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)] transition-colors">{label}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
              <MessageSquare size={12} /> Quick comment
            </div>
            <div className="flex items-end gap-1.5">
              <textarea value={comment} rows={2} data-testid="task-quick-actions-comment-input"
                onChange={(e) => { setComment(e.target.value); setPosted(false); }}
                placeholder="Add a comment…"
                className="n-focus max-h-24 min-h-[44px] flex-1 resize-none rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
              <button type="button" onClick={submitComment} disabled={posting || !comment.trim()} data-testid="task-quick-actions-comment-submit"
                title="Post comment"
                className="n-focus inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[var(--n-accent)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                {posting ? <Loader2 size={15} className="animate-spin" /> : posted ? <Check size={15} /> : <Send size={15} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </SmartPopover>
  );
};

// ---- RichTaskCard — full-parity grid card (adapted from Tasks renderAllCard)
// Props:
//   task              — planner feed task (enriched: project, shared_*, counts)
//   onOpen(task)      — open the task (Planner navigates to /notes/tasks?task=id)
//   onQuickActions({x,y}, task) — open the right-click / long-press menu
//   onOpenSubtasks(anchorRect, task, canEdit) — open the subtask popover
export const RichTaskCard = ({ task: t, onOpen, onQuickActions, onOpenSubtasks }) => {
  const longPressRef = useRef({ timer: null, fired: false, x: 0, y: 0 });
  const st = dueStatusOf(t);
  const dateLabel = t.is_completed
    ? (t.due_at ? new Date(t.due_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : 'No date')
    : st === 'none' ? 'No date'
      : st === 'today' ? 'Today'
        : (st === 'overdue' ? 'Overdue · ' : '') + new Date(t.due_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const metaColor = st === 'overdue' ? 'text-[var(--n-danger)]' : st === 'today' ? 'text-[var(--n-success)]' : 'text-[var(--n-muted)]';
  const proj = t.project;
  const sharedWithMe = !!t.shared_by;
  const showProjectChip = proj && proj.name && (!sharedWithMe || t.share_scope === 'project');
  const canEdit = (!t.shared_by || t.permission === 'edit');

  const openSubtasks = (e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    onOpenSubtasks({ top: r.top, bottom: r.bottom, left: r.left, right: r.right }, t, canEdit);
  };

  return (
    <div role="button" tabIndex={0} data-testid="planner-task-card"
      onClick={(e) => { if (longPressRef.current.fired) { longPressRef.current.fired = false; e.preventDefault(); e.stopPropagation(); return; } onOpen(t); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(t); } }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onQuickActions({ x: e.clientX, y: e.clientY }, t); }}
      onTouchStart={(e) => {
        if (e.touches.length !== 1) return;
        const tp = e.touches[0];
        const lp = longPressRef.current;
        lp.fired = false; lp.x = tp.clientX; lp.y = tp.clientY;
        if (lp.timer) clearTimeout(lp.timer);
        lp.timer = window.setTimeout(() => {
          lp.fired = true; lp.timer = null;
          try { if (navigator.vibrate) navigator.vibrate(12); } catch { /* noop */ }
          onQuickActions({ x: lp.x, y: lp.y }, t);
        }, 500);
      }}
      onTouchMove={(e) => {
        const lp = longPressRef.current;
        if (!lp.timer) return;
        const tp = e.touches[0];
        if (Math.abs(tp.clientX - lp.x) > 10 || Math.abs(tp.clientY - lp.y) > 10) { clearTimeout(lp.timer); lp.timer = null; }
      }}
      onTouchEnd={() => { const lp = longPressRef.current; if (lp.timer) { clearTimeout(lp.timer); lp.timer = null; } }}
      style={{ WebkitTouchCallout: 'none' }}
      className={`group relative cursor-pointer overflow-hidden rounded-xl border-[1.5px] bg-[var(--n-surface)] px-2.5 py-2.5 text-left outline-none transition-colors hover:bg-[var(--n-canvas-2)] focus-visible:ring-2 focus-visible:ring-[var(--n-accent)] ${dueBorderCls(st)}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-accent)]'}`} />
      <div className="flex items-stretch gap-2">
        <div className="min-w-0 flex-1">
          <div className={`line-clamp-2 break-words text-[13px] font-medium leading-snug ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]">
            <span className={`min-w-0 break-words font-medium ${metaColor}`}>{dateLabel}</span>
            {showProjectChip && (
              <span data-testid="task-card-project-chip"
                className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                style={{ color: proj.color || 'var(--n-accent)', borderColor: proj.color ? `${proj.color}59` : 'var(--n-border)', background: proj.color ? `${proj.color}1f` : 'transparent' }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: proj.color || 'var(--n-accent)' }} />
                <span className="max-w-[110px] truncate">{proj.name}</span>
              </span>
            )}
            {sharedWithMe && (
              <span data-testid="task-card-shared-chip" title={`Shared by ${t.shared_by}`}
                className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ color: N_GOLD, borderColor: N_GOLD_BORDER, background: N_GOLD_BG }}>
                <Users2 size={9} />
                <span className="max-w-[120px] truncate">{t.shared_by}</span>
              </span>
            )}
            <span className="ml-auto flex shrink-0 items-center gap-1.5">
              {t.linked_notes_count > 0 && (
                <span data-testid="task-card-linked-notes"
                  title={`${t.linked_notes_count} linked note${t.linked_notes_count > 1 ? 's' : ''}`}
                  className="inline-flex items-center gap-0.5 text-[var(--n-muted-2)]">
                  <NotebookText size={11} />{t.linked_notes_count > 1 ? t.linked_notes_count : ''}
                </span>
              )}
              {t.comment_count > 0 && (
                <span data-testid="task-card-comment-count" data-unread={t.unread_mention ? 'true' : 'false'}
                  title={t.unread_mention ? `${t.comment_count} comment(s) · you were mentioned` : `${t.comment_count} comment(s)`}
                  className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 transition-colors ${t.unread_mention
                    ? 'bg-[var(--n-accent-bg)] font-bold text-[var(--n-accent)]'
                    : 'text-[var(--n-muted-2)]'}`}>
                  {t.unread_mention ? <AtSign size={11} /> : <MessageSquare size={11} />}
                  {t.comment_count}
                </span>
              )}
              {t.subtask_total > 0 ? (
                <button type="button" data-testid="task-card-subtask-count"
                  onClick={openSubtasks}
                  title={`${t.subtask_done}/${t.subtask_total} sub-tasks done · tap to view`}
                  className={`n-focus inline-flex items-center rounded-full px-1 py-0.5 transition-colors hover:bg-[var(--n-canvas-2)] ${t.subtask_done === t.subtask_total ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:text-[var(--n-accent)]'}`}>
                  <ListChecks size={13} />
                </button>
              ) : canEdit ? (
                <button type="button" data-testid="task-card-subtask-add"
                  onClick={openSubtasks}
                  title="Add sub-tasks"
                  className="n-focus inline-flex items-center rounded-full px-1 py-0.5 text-[var(--n-muted-2)] transition-colors hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]">
                  <ListChecks size={12} />
                </button>
              ) : null}
            </span>
          </div>
          {(t.tags || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {t.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="inline-flex items-center gap-0.5 rounded-full border border-[var(--n-border)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted)]"><Hash size={9} />{tag}</span>
              ))}
              {t.tags.length > 3 && <span className="text-[10px] text-[var(--n-muted-2)]">+{t.tags.length - 3}</span>}
            </div>
          )}
        </div>
        {t.is_shared && <AvatarColumn people={t.shared_with} size={18} />}
      </div>
    </div>
  );
};
